import React, { useState, useEffect } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import {
  Btn,
  CustomDropdown,
  CustomModal,
  InputNumeroInteiro,
  InputPhone,
  CustomInputTextArea,
  SectionTitle,
} from '../../../../../components'
import { getEmissoresByTps } from '../../../../../services/api/https/settings/emissor-cte.http'
import {
  postEmitCTe,
  postEmitCTeComplementar,
  getRegrasFreteCteComplementar,
} from '../../../../../services/api/https/deliveries/cte.http'
import { getCfops } from '../../../../../services/api/https/settings/cfop.http'
import { somenteDigitos } from '../../../../../services/util/documento.service'
import { getValorFrete } from '../../../../../services/api/https/registration/regras-frete.http'
import { CTE_TOMADORES } from '../../../../../constants/cte-sem-nota'
import RadioGroup from '../../Cte/EmitirCte/CteSemNota/RadioGroup'
import ValoresFiscaisCte from '../../Cte/ValoresFiscaisCte/ValoresFiscaisCte'
import {
  ERROS_VALORES_FISCAIS,
  VALORES_FISCAIS_INICIAIS,
  aplicarTotal,
  erroValoresFiscais,
  montarComponentesValor,
  novoComponente,
  somaComponentes,
  toNum,
} from '../../Cte/ValoresFiscaisCte/valoresFiscaisCte.util'

function formatarValor(v) {
  const n = toNum(v)
  return n != null ? n.toFixed(2).replace('.', ',') : ''
}

const inicialState = {
  emissor_id: null,
  cfop_id: null,
  numero_ordem_coleta: null,
  ...VALORES_FISCAIS_INICIAIS,
  observacoes: null,
}

const errors = {
  emissor_id: 'O campo Emissor é obrigatório',
  cfop_id: 'O campo CFOP é obrigatório',
  numero_ordem_coleta: 'O campo Nº Ordem Coleta é obrigatório',
  ...ERROS_VALORES_FISCAIS,
}

export default function ModalEmitirCte({
  selectedTp,
  visible,
  delivery,
  // Emissão consolidada (CTe Empresa-Cliente): { deliveries_ids, frete, componentes, faixa_km_name, regras }
  consolidado = null,
  // CTe Complementar: linha da tela 3.18 do CTe autorizado a complementar (mesmo emissor e partes)
  cteComplementado = null,
  vehicleBadge,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(inicialState)
  const [emissores, setEmissores] = useState({ emissores: [], selected: null })
  const [cfops, setCfops] = useState({ cfops: [], selected: null })

  // Componentes do Valor da Prestação
  const [componentes, setComponentes] = useState([])
  const [faixaKmName, setFaixaKmName] = useState(null)
  const [regrasFaixa, setRegrasFaixa] = useState([])
  const [showRegrasModal, setShowRegrasModal] = useState(false)
  const [avisosRegras, setAvisosRegras] = useState([])

  const isComplementar = Boolean(cteComplementado)
  const isSubcontratacao = isComplementar && cteComplementado.document_origin === 'cte'
  const [telefoneTomador, setTelefoneTomador] = useState('')
  // Tomador do serviço na emissão com notas: Remetente (Empresa) ou Destinatário (Cliente)
  const [tomador, setTomador] = useState('0')

  useEffect(() => {
    // O modal fica montado entre aberturas: uma resposta atrasada de uma abertura anterior não
    // pode sobrescrever os dados do CTe atual
    let aberturaAtiva = true

    function aplicarDadosIniciais(frete, componentesIniciais, faixaKm, regras) {
      const comps = (componentesIniciais || []).map(c => novoComponente(c.nome, c.valor))
      setComponentes(comps)
      setFaixaKmName(faixaKm)
      setRegrasFaixa(regras || [])
      setState(prevState => ({
        ...prevState,
        valor_total: frete,
        valor_receber: frete,
        icms_base_calculo: frete,
        ibs_cbs_base_calculo: frete,
        observacoes: vehicleBadge ? `Veículo ${vehicleBadge}` : '',
      }))
    }

    async function fetchData() {
      try {
        const valorFrete = await getValorFrete(delivery.delivery_id)
        aplicarDadosIniciais(
          valorFrete.frete,
          valorFrete.componentes,
          valorFrete.faixa_km_name,
          valorFrete.componentes,
        )
      } catch (error) {
        console.error('Erro ao buscar valor do frete:', error)
      }
    }

    // Regras de Frete (tela 2.15) das notas do CTe complementado, para compor os componentes
    async function fetchRegrasComplementar() {
      try {
        const dados = await getRegrasFreteCteComplementar(cteComplementado.id)
        if (!aberturaAtiva) return
        setFaixaKmName(dados.faixa_km_name)
        setRegrasFaixa(dados.regras || [])
        setAvisosRegras(dados.avisos || [])
      } catch (error) {
        console.error('Erro ao buscar regras de frete do CTe complementado:', error)
        if (aberturaAtiva) setAvisosRegras([String((error && error.message) || error)])
      }
    }

    if (!visible) return
    // O tomador volta ao Remetente a cada abertura: uma emissão não herda a escolha da anterior
    setTomador('0')
    if (isComplementar) {
      // O complementar parte zerado: só os componentes informados compõem o valor. Emissor, CFOP,
      // situações tributárias e alíquotas vêm do CTe complementado (a SEFAZ exige o mesmo emitente
      // e o mesmo CST); o modal é reaberto para CTes diferentes, então nada do anterior é mantido.
      setComponentes([])
      setFaixaKmName(null)
      setRegrasFaixa([])
      setAvisosRegras([])
      setShowRegrasModal(false)
      setTelefoneTomador(cteComplementado.cte_origem_telefone || '')
      setState({
        ...inicialState,
        emissor_id: cteComplementado.emissor_id,
        cfop_id: cteComplementado.cfop_id,
        numero_ordem_coleta: cteComplementado.numero_ordem_coleta,
        icms_situacao_tributaria: cteComplementado.icms_situacao_tributaria || null,
        icms_aliquota: toNum(cteComplementado.icms_aliquota),
        ibs_cbs_situacao_tributaria: cteComplementado.ibs_cbs_situacao_tributaria || null,
        ibs_cbs_classificacao_tributaria: cteComplementado.ibs_cbs_classificacao_tributaria || null,
        ibs_uf_aliquota: toNum(cteComplementado.ibs_uf_aliquota),
        ibs_mun_aliquota: toNum(cteComplementado.ibs_mun_aliquota),
        cbs_aliquota: toNum(cteComplementado.cbs_aliquota),
        observacoes: `CT-e complementar ao CT-e nº ${cteComplementado.numero} série ${cteComplementado.serie}. Motivo: `,
      })
      fetchRegrasComplementar()
      return () => {
        aberturaAtiva = false
      }
    }
    if (consolidado) {
      // Dados já calculados no modal Emitir CTe Empresa-Cliente
      aplicarDadosIniciais(
        consolidado.frete,
        consolidado.componentes,
        consolidado.faixa_km_name,
        consolidado.regras,
      )
    } else if (delivery) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delivery, consolidado, cteComplementado, visible, vehicleBadge])

  useEffect(() => {
    async function fetchData() {
      const [emissores, cfops] = await Promise.all([getEmissoresByTps(selectedTp), getCfops()])

      // Mapear CFOPs para adicionar campo concatenado para facilitar a busca
      const cfopsMapeados = cfops.map(cfop => ({
        ...cfop,
        codigoNatureza: `${cfop.codigo} - ${cfop.natureza}`
      }))

      // Encontrar as opções selecionadas anteriormente no state
      const emissorSelecionado = emissores.find(item => item.id === state.emissor_id)
      const cfopSelecionado = cfopsMapeados.find(item => item.id === state.cfop_id)

      setEmissores(prevState => ({
        ...prevState,
        emissores,
        selected: emissorSelecionado || null,
      }))
      setCfops(prevState => ({
        ...prevState,
        cfops: cfopsMapeados,
        selected: cfopSelecionado || null,
      }))
    }
    fetchData()
  }, [selectedTp, state.emissor_id, state.cfop_id])

  // A Regra de Frete escolhida vira um componente do valor e entra no total do frete
  const onRegraSelecionada = regra => {
    const novos = [...componentes, novoComponente(regra.nome, regra.valor)]
    setComponentes(novos)
    setState(prevState => aplicarTotal(prevState, somaComponentes(novos)))
    setShowRegrasModal(false)
  }

  // Events
  const onBtnSalvarClicked = async () => {
    try {
      Object.keys(state).forEach(key => {
        if (key !== 'observacoes' && state[key] === null) {
          throw new Error(errors[key])
        }
      })

      if (state.observacoes && state.observacoes.length > 2000) {
        throw new Error('O campo Observações deve ter no máximo 2000 caracteres')
      }

      const erroFiscal = erroValoresFiscais(state)
      if (erroFiscal) throw new Error(erroFiscal)

      setIsLoading(true)

      const componentesValor = montarComponentesValor(componentes)

      if (isComplementar) {
        if (!componentesValor.length) {
          throw new Error('Adicione ao menos um Componente do Valor para o CTe Complementar')
        }
        if (isSubcontratacao) {
          const telefone = somenteDigitos(telefoneTomador)
          if (telefone.length < 6 || telefone.length > 14) {
            throw new Error(
              'Informe o Telefone do Tomador (emitente do CTe de origem) com 6 a 14 dígitos',
            )
          }
        }
        await postEmitCTeComplementar({
          ...state,
          cte_id: cteComplementado.id,
          componentes_valor: componentesValor,
          ...(isSubcontratacao ? { telefone_tomador: somenteDigitos(telefoneTomador) } : {}),
        })
        onModalSave(cteComplementado.id)
        return
      }

      const deliveriesIds = consolidado ? consolidado.deliveries_ids : [delivery.delivery_id]

      await postEmitCTe({
        ...state,
        tomador,
        deliveries_ids: deliveriesIds,
        ...(componentesValor.length ? { componentes_valor: componentesValor } : {}),
      })

      onModalSave(deliveriesIds[0])
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Documento de referência do CTe complementado, conforme a origem documental dele
  const renderDocumentoComplementado = () => {
    if (isSubcontratacao) {
      return (
        <div>
          <b>CT-e de origem:</b> nº {cteComplementado.cte_origem_numero} de{' '}
          {cteComplementado.cte_origem_emitente}
          <span style={{ color: 'gray' }}> (CTe de Subcontratação)</span>
        </div>
      )
    }
    if (cteComplementado.document_origin === 'outros') {
      return (
        <div>
          <b>Documento(s):</b> {cteComplementado.documento_descricao || '-'}
          <span style={{ color: 'gray' }}> (CTe sem Nota Fiscal)</span>
        </div>
      )
    }
    return (
      <div>
        <b>Nota(s) Fiscal(is):</b> {cteComplementado.identifier}
      </div>
    )
  }

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={isComplementar ? 'Emitir CTe Complementar' : 'Emitir CTe'}
      width={800}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        {isComplementar && (
          <div className="p-col-12">
            <SectionTitle title="CTe Complementado" />
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div>
                <b>CTe:</b> nº {cteComplementado.numero} - série {cteComplementado.serie}
                {cteComplementado.globalizado ? ' (globalizado)' : ''}
              </div>
              <div>
                <b>Chave:</b> {cteComplementado.chave}
              </div>
              <div>
                <b>Cliente:</b> {cteComplementado.client_name}
                {cteComplementado.client_city ? ` - ${cteComplementado.client_city}` : ''}
              </div>
              <div>
                <b>Empresa:</b> {cteComplementado.company_name}
              </div>
              {renderDocumentoComplementado()}
              <div>
                <b>Valor do frete original:</b> R$ {formatarValor(cteComplementado.valor_total)}
              </div>
            </div>
            <span style={{ color: 'gray', fontSize: 12 }}>
              O CTe Complementar repete emissor, remetente, destinatário e tomador do CTe original;
              informe apenas os valores a complementar.
            </span>
          </div>
        )}
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="razao_social"
            options={emissores.emissores}
            value={emissores.selected}
            label="Emissor:*"
            filter={true}
            disabled={isComplementar}
            onChange={value => {
              setState(prevState => {
                prevState.emissor_id = value.id
                return { ...prevState }
              })
              setEmissores(prevState => ({ ...prevState, selected: value }))
            }}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="codigoNatureza"
            options={cfops.cfops}
            value={cfops.selected}
            label="CFOP:*"
            filter={true}
            onChange={value => {
              setState(prevState => {
                prevState.cfop_id = value.id
                return { ...prevState }
              })
              setCfops(prevState => ({ ...prevState, selected: value }))
            }}
            tooltip={'Código Fiscal de Operações e Prestações'}
          />
        </div>
        {!isComplementar && (
          <div className="p-md-6 p-col-12">
            <InputNumeroInteiro
              label="Nº Ordem Coleta:*"
              marginTop={10}
              value={state.numero_ordem_coleta}
              onChange={value => setState({ ...state, numero_ordem_coleta: value })}
            />
            <span style={{ color: 'gray', fontSize: 12 }}>
              Número gerado pela transportadora associado a ordem de coleta
            </span>
          </div>
        )}
        {isSubcontratacao && (
          <div className="p-md-6 p-col-12">
            <InputPhone
              label="Telefone do tomador:*"
              marginTop={10}
              mask="(99)9999-9999?9"
              autoClear={false}
              value={telefoneTomador}
              onChange={value => setTelefoneTomador(value || '')}
            />
            <span style={{ color: 'gray', fontSize: 12 }}>
              Emitente do CTe de origem, tomador do CTe de Subcontratação
            </span>
          </div>
        )}
        {!isComplementar && (
          <div className="p-col-12">
            <SectionTitle title="Tomador do Serviço" />
            <div style={{ marginTop: 5 }}>
              <RadioGroup
                nome="tomador-emissao-cte"
                items={CTE_TOMADORES}
                value={tomador}
                onChange={setTomador}
              />
            </div>
            <span style={{ color: 'gray', fontSize: 12 }}>
              Quem paga o frete: o Remetente (Empresa da nota) ou o Destinatário (Cliente).
            </span>
          </div>
        )}

        <ValoresFiscaisCte
          key={visible ? 'aberto' : 'fechado'}
          state={state}
          onStateChange={setState}
          componentes={componentes}
          onComponentesChange={setComponentes}
          onError={onError}
          componentesEmptyMessage={
            isComplementar
              ? 'Nenhum componente. Adicione um Novo Campo ou uma Regra de Frete com o valor a complementar.'
              : 'Nenhum componente. Os valores do frete podem ser digitados diretamente nos campos abaixo.'
          }
          componentesActions={
            <Btn onClick={() => setShowRegrasModal(true)}>Nova Regra de Frete</Btn>
          }
        />

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={4}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>

      {/* Regras de Frete da Faixa de KM da nota */}
      <CustomModal
        isVisible={showRegrasModal}
        header={`Regras de Frete${faixaKmName ? ` - ${faixaKmName}` : ''}`}
        width={600}
        onClose={() => setShowRegrasModal(false)}
      >
        <span style={{ color: 'gray', fontSize: 12 }}>
          Clique na regra desejada para adicioná-la aos Componentes do Valor
        </span>
        {avisosRegras.map((aviso, index) => (
          <div key={index} style={{ color: '#e67e22', fontSize: 12, marginTop: 5 }}>
            {aviso}
          </div>
        ))}
        <DataTable
          value={regrasFaixa}
          selectionMode="single"
          onRowClick={e => onRegraSelecionada(e.data)}
          emptyMessage={
            avisosRegras.length
              ? 'Nenhuma regra de frete disponível para este CTe'
              : 'Nenhuma regra de frete cadastrada para a faixa de KM dessa cidade'
          }
          style={{ marginTop: 10 }}
        >
          <Column field="nome" header="Regra de Faturamento" />
          <Column field="tipo" header="Tipo" style={{ width: 140 }} />
          <Column field="valor" header="Valor" style={{ width: 140 }} body={r => formatarValor(r.valor)} />
        </DataTable>
      </CustomModal>
    </CustomModal>
  )
}
